import React, { useState, useEffect, useCallback } from 'react';
import { PersonFinal } from '@/api/entities';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Search, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/components/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

export default function PeopleClientHistory() {
    const [people, setPeople] = useState([]);
    const [filteredPeople, setFilteredPeople] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editedPeople, setEditedPeople] = useState({});
    const [savingIds, setSavingIds] = useState(new Set());
    const [savedIds, setSavedIds] = useState(new Set());
    const { toast } = useToast();

    const loadPeople = useCallback(async () => {
        setIsLoading(true);
        try {
            const allPeople = await PersonFinal.list('-persona_fit_score');
            setPeople(allPeople);
            setFilteredPeople(allPeople);
        } catch (error) {
            console.error('Error loading people:', error);
            toast({
                variant: 'destructive',
                title: 'Error',
                description: 'Failed to load people data.'
            });
        }
        setIsLoading(false);
    }, [toast]);

    useEffect(() => {
        loadPeople();
    }, [loadPeople]);

    useEffect(() => {
        const filtered = people.filter(person => {
            const fullName = `${person.first_name} ${person.last_name}`.toLowerCase();
            const accountName = (person.account_name || '').toLowerCase();
            const search = searchTerm.toLowerCase();
            return fullName.includes(search) || accountName.includes(search);
        });
        setFilteredPeople(filtered);
    }, [searchTerm, people]);

    const handleFieldChange = (personId, field, value) => {
        setEditedPeople(prev => ({
            ...prev,
            [personId]: {
                ...prev[personId],
                [field]: value
            }
        }));
        
        // Remove from saved state if editing again
        setSavedIds(prev => {
            const next = new Set(prev);
            next.delete(personId);
            return next;
        });
    };

    const handleSave = async (person) => {
        const edits = editedPeople[person.id];
        if (!edits || Object.keys(edits).length === 0) {
            return;
        }

        setSavingIds(prev => new Set(prev).add(person.id));

        try {
            await PersonFinal.update(person.id, edits);
            
            // Update local state
            setPeople(prev => prev.map(p => 
                p.id === person.id ? { ...p, ...edits } : p
            ));
            
            // Clear edits for this person
            setEditedPeople(prev => {
                const next = { ...prev };
                delete next[person.id];
                return next;
            });

            // Mark as saved
            setSavedIds(prev => new Set(prev).add(person.id));
            setTimeout(() => {
                setSavedIds(prev => {
                    const next = new Set(prev);
                    next.delete(person.id);
                    return next;
                });
            }, 2000);

            toast({
                title: 'Saved',
                description: `Updated ${person.first_name} ${person.last_name}'s client history.`
            });
        } catch (error) {
            console.error('Error saving:', error);
            toast({
                variant: 'destructive',
                title: 'Error',
                description: 'Failed to save changes.'
            });
        } finally {
            setSavingIds(prev => {
                const next = new Set(prev);
                next.delete(person.id);
                return next;
            });
        }
    };

    const getCurrentValue = (person, field) => {
        return editedPeople[person.id]?.[field] !== undefined 
            ? editedPeople[person.id][field] 
            : person[field];
    };

    const hasChanges = (personId) => {
        return editedPeople[personId] && Object.keys(editedPeople[personId]).length > 0;
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-[#0A1A2F]">People Client History</h2>
                    <p className="text-sm text-[#5C6B7A] mt-1">Manage client relationships and employment history</p>
                </div>
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                    placeholder="Search by name or account..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                />
            </div>

            <div className="space-y-4">
                {filteredPeople.map(person => {
                    const isSaving = savingIds.has(person.id);
                    const isSaved = savedIds.has(person.id);
                    const hasEdits = hasChanges(person.id);

                    return (
                        <div key={person.id} className="bg-white border rounded-lg p-4 shadow-sm">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h3 className="font-semibold text-[#0A1A2F]">
                                        {person.first_name} {person.last_name}
                                    </h3>
                                    <p className="text-sm text-[#5C6B7A]">{person.role_title}</p>
                                    <p className="text-sm text-[#5C6B7A]">{person.account_name}</p>
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => handleSave(person)}
                                    disabled={!hasEdits || isSaving}
                                    className={`${isSaved ? 'bg-green-600 hover:bg-green-700' : 'bg-[#00C2C2] hover:bg-[#00A3A3]'}`}
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Saving...
                                        </>
                                    ) : isSaved ? (
                                        <>
                                            <CheckCircle2 className="w-4 h-4 mr-2" />
                                            Saved
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4 mr-2" />
                                            Save Changes
                                        </>
                                    )}
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-[#0A1A2F] mb-1 block">
                                        Worked for Customer
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            checked={getCurrentValue(person, 'worked_for_customer') || false}
                                            onCheckedChange={(checked) => 
                                                handleFieldChange(person.id, 'worked_for_customer', checked)
                                            }
                                        />
                                        <span className="text-sm text-[#5C6B7A]">
                                            Yes, this person worked for an existing client
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-[#0A1A2F] mb-1 block">
                                        Client Name(s)
                                    </label>
                                    <Input
                                        value={getCurrentValue(person, 'client_name') || ''}
                                        onChange={(e) => 
                                            handleFieldChange(person.id, 'client_name', e.target.value)
                                        }
                                        placeholder="e.g., University of Sydney, RMIT"
                                        className="text-sm"
                                    />
                                    <p className="text-xs text-[#5C6B7A] mt-1">
                                        Separate multiple clients with semicolons
                                    </p>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-sm font-medium text-[#0A1A2F] mb-1 block">
                                        Former Employers
                                    </label>
                                    <Textarea
                                        value={
                                            Array.isArray(getCurrentValue(person, 'former_employers'))
                                                ? getCurrentValue(person, 'former_employers').join('\n')
                                                : (getCurrentValue(person, 'former_employers') || '')
                                        }
                                        onChange={(e) => {
                                            const lines = e.target.value.split('\n').filter(line => line.trim());
                                            handleFieldChange(person.id, 'former_employers', lines);
                                        }}
                                        placeholder="Enter one employer per line"
                                        className="text-sm min-h-[80px]"
                                    />
                                    <p className="text-xs text-[#5C6B7A] mt-1">
                                        One employer per line
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {filteredPeople.length === 0 && (
                    <div className="text-center py-12 text-[#5C6B7A]">
                        <p>No people found matching your search.</p>
                    </div>
                )}
            </div>
        </div>
    );
}